/**
 * confirm-copy: what the /admin portal's ConfirmDialogs ask before an action
 * runs (they replaced the browser's native confirm). Each returns the
 * question as the title, one sentence of consequence, the confirm label, and
 * `danger` for a destructive action, which turns the confirm button Coral
 * (DESIGN.md "Semantic": destructive actions are a blocking-error class).
 *
 * Routine decisions (approving an organization) stay the ordinary primary.
 */
import type { ConfirmDialogProps } from '@/components/carbon/ConfirmDialog'

export type ConfirmCopy = Pick<ConfirmDialogProps, 'title' | 'body' | 'confirmLabel' | 'danger'>

export type OrgDecision = 'approve' | 'deactivate'

/** Organization drawer: Approve (routine) and Deactivate (blocks sign-in, danger). */
export function orgDecisionCopy(decision: OrgDecision, name: string): ConfirmCopy {
  return decision === 'approve'
    ? {
        title: `Approve ${name}?`,
        body: 'The organization becomes active, and its people can sign in to Guardian.',
        confirmLabel: 'Approve',
      }
    : {
        title: `Deactivate ${name}?`,
        body: 'Its people can no longer sign in to Guardian.',
        confirmLabel: 'Deactivate',
        danger: true,
      }
}

/** Organizations tab, waiting-for-review queue: Reject declines the access request (danger). */
export function orgRejectCopy(name: string): ConfirmCopy {
  return {
    title: `Reject ${name}?`,
    body: 'Guardian declines the access request, so its people cannot sign in.',
    confirmLabel: 'Reject',
    danger: true,
  }
}

/** Person drawer: Deactivate removes their access (danger). */
export function personDeactivateCopy(name: string): ConfirmCopy {
  return {
    title: `Deactivate ${name}?`,
    body: 'They lose access to Guardian the next time they sign in.',
    confirmLabel: 'Deactivate',
    danger: true,
  }
}

export type TenantDecision = 'revoke-consent' | 'deactivate'

/** Tenant drawer: Revoke consent and Deactivate both cut Guardian's access to the tenant (danger). */
export function tenantDecisionCopy(decision: TenantDecision, name: string): ConfirmCopy {
  return decision === 'revoke-consent'
    ? {
        title: `Revoke admin consent for ${name}?`,
        body: 'Guardian switches this tenant back to per-tenant authentication.',
        confirmLabel: 'Revoke consent',
        danger: true,
      }
    : {
        title: `Deactivate ${name}?`,
        body: 'Guardian stops scanning this tenant.',
        confirmLabel: 'Deactivate',
        danger: true,
      }
}
